import * as RadixDialog from "@radix-ui/react-dialog";
import { X } from "@phosphor-icons/react";
import { cn } from "@/lib/cn";
import { IconButton } from "@/components/primitives/Button";
import { BfrsPortalScope } from "@/components/ui/providers/BfrsProvider";
import type { DrawerPlacement, DrawerProps, DrawerSize } from "./Drawer.types";

const placementClassName: Record<DrawerPlacement, string> = {
  right: "bfrs-right-0 bfrs-top-0 bfrs-h-dvh bfrs-animate-drawer-right-in",
  left: "bfrs-left-0 bfrs-top-0 bfrs-h-dvh bfrs-animate-drawer-left-in",
  bottom: "bfrs-bottom-0 bfrs-left-0 bfrs-max-h-[85dvh] bfrs-w-full bfrs-rounded-t-xl bfrs-animate-drawer-bottom-in"
};

const sizeClassName: Record<DrawerSize, string> = {
  sm: "bfrs-w-full bfrs-max-w-[360px]",
  md: "bfrs-w-full bfrs-max-w-md",
  lg: "bfrs-w-full bfrs-max-w-3xl",
  xl: "bfrs-w-full bfrs-max-w-[min(1400px,75vw)]"
};

export function Drawer({
  open,
  defaultOpen,
  onOpenChange,
  onClose,
  trigger,
  title,
  description,
  children,
  footer,
  placement = "right",
  size = "md",
  closeButton = true,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  preventClose = false,
  className,
  contentClassName,
  headerClassName,
  footerClassName
}: DrawerProps) {
  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen && preventClose) return;
    onOpenChange?.(nextOpen);
    if (!nextOpen) onClose?.();
  };

  const resolvedSize = placement === "bottom" ? "w-full" : sizeClassName[size];

  return (
    <RadixDialog.Root open={open} defaultOpen={defaultOpen} onOpenChange={handleOpenChange}>
      {trigger && <RadixDialog.Trigger asChild>{trigger}</RadixDialog.Trigger>}
      <RadixDialog.Portal>
        <BfrsPortalScope>
          <RadixDialog.Overlay className="bfrs-fixed bfrs-inset-0 bfrs-z-50 bfrs-bg-text-primary/30 bfrs-animate-overlay-in" />
          <RadixDialog.Content
            className={cn(
              "bfrs-fixed bfrs-z-50 bfrs-flex bfrs-flex-col bfrs-overflow-hidden bfrs-border-border bfrs-bg-surface-card bfrs-shadow-overlay bfrs-outline-none",
              placement === "bottom" ? "bfrs-border-t" : "bfrs-border-l",
              placementClassName[placement],
              resolvedSize,
              className,
              contentClassName
            )}
            onPointerDownOutside={(event) => {
              if (!closeOnOverlayClick || preventClose) event.preventDefault();
            }}
            onEscapeKeyDown={(event) => {
              if (!closeOnEscape || preventClose) event.preventDefault();
            }}
          >
            {(title || description || closeButton) && (
              <div className={cn("bfrs-flex bfrs-items-start bfrs-gap-4 bfrs-border-b bfrs-border-border bfrs-px-5 bfrs-py-4", headerClassName)}>
                <div className="bfrs-min-w-0 bfrs-flex-1">
                  {title && <RadixDialog.Title className="bfrs-text-base bfrs-font-bold bfrs-text-text-primary">{title}</RadixDialog.Title>}
                  {description && <RadixDialog.Description className="bfrs-mt-1 bfrs-text-sm bfrs-leading-5 bfrs-text-text-muted">{description}</RadixDialog.Description>}
                </div>
                {closeButton && (
                  <RadixDialog.Close asChild>
                    <IconButton aria-label="Close drawer" icon={<X size={16} />} variant="outline" size="sm" disabled={preventClose} />
                  </RadixDialog.Close>
                )}
              </div>
            )}
            <div className="bfrs-min-h-0 bfrs-flex-1 bfrs-overflow-auto bfrs-px-5 bfrs-py-4">{children}</div>
            {footer && <div className={cn("bfrs-border-t bfrs-border-border bfrs-bg-surface-card bfrs-px-5 bfrs-py-4", footerClassName)}>{footer}</div>}
          </RadixDialog.Content>
        </BfrsPortalScope>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
